<template>
  <div class="registration-form__step registration-form__step-address">
    <div class="registration-form__step__container">
      <div class="data">
        <template v-if="sectorRequiresInput">
          <div
            v-if="
              (requiresInputForCraftsman || requiresInputForRetailer) &&
              selectSubTargetGroupList.length > 0
            "
            class="data-item retailer-list"
          >
            <select-input-fi
              :label="subTargetGroupTitle"
              :value="subTargetGroup"
              :options="selectSubTargetGroupList"
              :rules="subTargetGroupRule"
              data-test="sub-target-group"
              :data-test-2="subTargetGroup"
              required
              @input="subTargetGroupListChanged"
              @open="enableSubTargetGroupValidation = true"
            />
          </div>

          <text-input-fi
            ref="company"
            v-model.trim="company"
            class="data-item company"
            name="company"
            data-test="company"
            :rules="rules.requiredAfterTrimmed"
            :maxlength="120"
            :label="fields.companyNameLabel?.value"
            required
          />

          <div
            v-if="!registrationWithoutTargetGroup"
            :class="{
              'data-item customer-number': true,
              'tooltip-wrapper': fields.customerIDInfoText?.value,
            }"
          >
            <text-input-fi
              ref="customerNumber"
              v-model.trim="customerNumber"
              name="customerNumber"
              data-test="customer-number"
              :rules="rules.noTags"
              :maxlength="13"
              :label="fields.customerIDLabel?.value"
            />
            <tooltip-fi
              v-if="fields.customerIDInfoText?.value"
              class="tooltip"
              :tooltip="fields.customerIDInfoText.value"
            />
          </div>
        </template>

        <text-input-fi
          ref="street"
          v-model.trim="street"
          class="data-item street"
          name="street"
          data-test="street"
          :rules="rules.street"
          :maxlength="120"
          :label="fields.streetLabel?.value"
          required
        />

        <text-input-fi
          ref="streetNumber"
          v-model.trim="streetNumber"
          class="data-item street-number"
          name="streetNumber"
          data-test="street-number"
          :rules="rules.streetnumber"
          :maxlength="20"
          :label="fields.streetNumberLabel?.value"
          required
        />

        <text-input-fi
          ref="zipcode"
          v-model.trim="zipcode"
          class="data-item zipcode"
          name="zipcode"
          data-test="zipcode"
          :rules="enableZipcodeValidation ? validateZip(zipcode, zipRegEx) : []"
          :maxlength="20"
          :label="fields.zipCodeLabel?.value"
          required
          @keydown="enableZipcodeValidationDefault = false"
          @blur="zipCodeBlurred"
        />

        <text-input-fi
          ref="city"
          v-model.trim="city"
          class="data-item city"
          name="city"
          data-test="city"
          :rules="rules.city"
          :maxlength="120"
          :label="fields.cityLabel?.value"
          required
        />

        <div
          v-if="selectCountryList.length > 0"
          class="data-item retailer-list"
        >
          <select-input-fi
            :label="fields.countryLabel?.value"
            :value="country"
            :options="selectCountryList"
            :rules="enableCountryValidation ? rules.country : []"
            :data-test-2="country"
            data-test="country"
            required
            @input="countryListChanged"
            @open="enableCountryValidation = true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SelectInputFi, TextInputFi, TooltipFi } from 'atoms';
import countryZipValidationMixin from 'mixins/countryZipValidationMixin';
import validatorsMixin from 'mixins/validatorsMixin';

import { getSelectList } from '@/utils/fieldTransformations';

export default {
  name: 'RegistrationStepAddress',
  components: {
    TooltipFi,
    SelectInputFi,
    TextInputFi,
  },
  mixins: [validatorsMixin, countryZipValidationMixin],
  inject: ['fields'],
  emits: ['valid-changed'],
  data() {
    return {
      isValid: false,
      countryList: [],
      fallbackCountries: [],
      fallbackSubTargetGroups: [],
      enableSubTargetGroupValidation: false,
      enableCountryValidation: false,
      enableZipcodeValidation: false,
      enableZipcodeValidationDefault: true,
    };
  },
  computed: {
    targetGroup() {
      return this.$store.getters['registration/getTargetGroup'];
    },
    registrationWithoutTargetGroup() {
      return this.fields.registrationType?.value === 'WithoutTargetGroup';
    },
    selectSubTargetGroupList() {
      if (this.requiresInputForCraftsman) {
        const [, subTargetGroupList] = getSelectList(
          this.fields,
          'subTargetGroupCraftList',
          [],
          true,
        );
        return subTargetGroupList || [];
      }
      if (this.requiresInputForRetailer) {
        const [, subTargetGroupList] = getSelectList(
          this.fields,
          'subTargetGroupRetailerList',
          [],
          true,
        );
        return subTargetGroupList || [];
      }

      return [];
    },
    selectCountryList() {
      return this.countryList;
    },
    subTargetGroupTitle() {
      if (this.requiresInputForCraftsman) {
        return this.fields.subTargetGroupCraftLabel?.value;
      }

      return this.fields.subTargetGroupRetailerLabel?.value;
    },
    subTargetGroupRule() {
      let rule = [];
      if (this.requiresInputForCraftsman && this.enableSubTargetGroupValidation) {
        rule = this.rules.craft;
      }
      if (this.requiresInputForRetailer && this.enableSubTargetGroupValidation) {
        rule = this.rules.retailer;
      }
      return rule;
    },
    subTargetGroup: {
      get() {
        const subTargetGroup = this.$store.getters['registration/getSubTargetGroup'];
        return subTargetGroup?.length ? subTargetGroup : null;
      },
      set(subTargetGroup) {
        const subTargetGroupTitle = this.selectSubTargetGroupList.find(
          (group) => group.value === subTargetGroup,
        )?.title;
        this.$store.dispatch('registration/setSubTargetGroup', subTargetGroup);
        this.$store.dispatch('registration/setSubTargetGroupTitle', subTargetGroupTitle);
        this.validate();
      },
    },
    country: {
      get() {
        return this.$store.getters['registration/getCountry'];
      },
      set(country) {
        this.$store.dispatch('registration/setCountry', country);
        this.validate();
      },
    },
    company: {
      get() {
        return this.$store.getters['registration/getCompany'];
      },
      set(company) {
        this.$store.dispatch('registration/setCompany', company);
        this.validate();
      },
    },
    customerNumber: {
      get() {
        return this.$store.getters['registration/getCustomerNumber'];
      },
      set(customerNumber) {
        this.$store.dispatch('registration/setCustomerNumber', customerNumber);
        this.validate();
      },
    },
    street: {
      get() {
        return this.$store.getters['registration/getStreet'];
      },
      set(street) {
        this.$store.dispatch('registration/setStreet', street);
        this.validate();
      },
    },
    streetNumber: {
      get() {
        return this.$store.getters['registration/getStreetNumber'];
      },
      set(streetNumber) {
        this.$store.dispatch('registration/setStreetNumber', streetNumber);
        this.validate();
      },
    },
    zipcode: {
      get() {
        return this.$store.getters['registration/getZipcode'] || '';
      },
      set(zipcode) {
        this.$store.dispatch('registration/setZipcode', zipcode.toLocaleUpperCase());
        this.validate();
      },
    },
    city: {
      get() {
        return this.$store.getters['registration/getCity'];
      },
      set(city) {
        this.$store.dispatch('registration/setCity', city);
        this.validate();
      },
    },
    validateAddress: {
      get() {
        return this.$store.getters['registration/getValidateAddress'];
      },
      set(validateAddress) {
        this.$store.dispatch('registration/setValidateAddress', validateAddress);
      },
    },
    sectorRequiresInput() {
      return this.targetGroup !== 'GROUP-diy';
    },
    requiresInputForCraftsman() {
      return this.targetGroup === 'GROUP-craftsman';
    },
    requiresInputForRetailer() {
      return this.targetGroup === 'GROUP-retailer';
    },
  },
  watch: {
    validateAddress(value) {
      if (value) {
        this.validate();
      }
    },
  },
  mounted() {
    const [defaultCountry, countryList] = getSelectList(this.fields, 'countryList');
    this.countryList = countryList?.sort((a, b) => a.name.localeCompare(b.name));
    if (!this.country && defaultCountry?.value) {
      this.countryListChanged(defaultCountry.value);
    }
    this.validate();
  },
  async created() {
    if (this.zipcode) {
      this.enableZipcodeValidation = true;
    }
  },
  methods: {
    subTargetGroupListChanged(value) {
      this.subTargetGroup = value;
    },
    countryListChanged(value) {
      this.setCountryZipValidation(value);
      this.country = value;
    },
    async validate() {
      // the errorBuckets are not computed, so we have no guarantee they are updated yet
      await this.$nextTick();

      if (this.$refs.street && this.$refs.streetNumber && this.$refs.zipcode && this.$refs.city) {
        let isSubTargetGroupValid = true;
        if (
          (this.requiresInputForCraftsman || this.requiresInputForRetailer) &&
          this.selectSubTargetGroupList.length > 0
        ) {
          isSubTargetGroupValid = Boolean(this.subTargetGroup);
        }

        let isCompanyValid = true;
        if (this.sectorRequiresInput) {
          isCompanyValid = Boolean(
            this.company !== '' && this.$refs.company && !this.$refs.company.errorBucket.length,
          );
        }

        this.isValid = Boolean(
          isSubTargetGroupValid &&
            isCompanyValid &&
            this.street !== '' &&
            !this.$refs.street.errorBucket.length &&
            this.streetNumber !== '' &&
            !this.$refs.streetNumber.errorBucket.length &&
            this.zipcode !== '' &&
            !this.$refs.zipcode.errorBucket.length &&
            this.enableZipcodeValidation &&
            this.city !== '' &&
            !this.$refs.city.errorBucket.length &&
            this.country &&
            this.country !== '',
        );
      } else {
        this.isValid = false;
      }
      this.validateAddress = false;
      this.$emit('valid-changed', this.isValid);
    },
    async zipCodeBlurred() {
      this.enableZipcodeValidation = true;
      await this.$nextTick();
      this.validate();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './registration-step-address';
</style>
